import { FC } from 'react';
import styled, { css } from 'styled-components';
import { media } from 'src/common/styles/breakpoints';
import { AspspInfoToolTip } from 'src/pages/AspspSelectPage/components/AspspInfoToolTip';
import { PublicAspspItemDTO } from 'src/pages/AspspSelectPage/aspspModels';

const AspspButton = styled.button<{
  $isAvailable: boolean;
  $isSelected: boolean;
  $isLoading: boolean;
}>`
  position: relative;
  background-color: ${({ theme }) => theme.aspsp.bg};
  width: 100%;
  min-height: 4.9375rem;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.aspsp.borderColor.default};
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  outline: none;
  font-size: 1rem;

  &:hover {
    cursor: pointer;
  }

  &:focus,
  &:acitve {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.focus};
    outline-offset: 1px;
  }

  ${({ theme, $isAvailable, $isSelected, $isLoading }) =>
    $isSelected &&
    $isAvailable &&
    !$isLoading &&
    css`
      border: 2px solid ${theme.aspsp.borderColor.selected};
    `};

  ${({ $isAvailable }) =>
    !$isAvailable &&
    css`
      &:hover {
        cursor: not-allowed;
      }
      pointer-events: none;
      opacity: 0.7;
    `};

  ${({ $isLoading }) =>
    $isLoading &&
    css`
      pointer-events: none;
      opacity: 0.7;
    `};

  ${media.w.min.px576(css`
    min-width: 170px;
    height: 5rem;
  `)}
`;

const Logo = styled.img<{ $isAvailable: boolean }>`
  object-fit: contain;
  max-height: 4.25rem;
  max-width: 8rem;

  ${({ $isAvailable }) =>
    !$isAvailable &&
    css`
      filter: grayscale(1) opacity(0.7);
    `};

  ${media.w.min.px576(css`
    max-width: 60%;
  `)}
`;

interface AspspListItemProps {
  aspsp?: PublicAspspItemDTO;
  isLoading?: boolean;
  onClick: () => void;
  selected: boolean;
  disableClick?: boolean;
  isAvailable?: boolean;
}

export const AspspListItem: FC<AspspListItemProps> = ({
  aspsp,
  onClick,
  selected: isSelected,
  isLoading = false,
  isAvailable = true,
}) => {
  // Button is disabled if not available or loading
  const isDisabled = !isAvailable || isLoading;

  return (
    <AspspButton
      type="button"
      aria-pressed={isSelected}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      $isAvailable={isAvailable}
      $isSelected={isSelected}
      $isLoading={isLoading}
    >
      {aspsp?.logo ? (
        <Logo src={aspsp.logo} alt={aspsp.name} $isAvailable={isAvailable} />
      ) : (
        <>{aspsp?.name}</>
      )}
      {!isAvailable && <AspspInfoToolTip />}
    </AspspButton>
  );
};
